/**
 * BRAND PALETTE — white-label "opzione C": la palette del tenant arriva dal
 * BACKEND al login (vedi themeApi.ts + themeSlice.ts). La logica "che tenant
 * è" vive lato backend (dedotta dal JWT), NON qui.
 *
 * Il frontend conosce soltanto:
 *   - la FORMA della palette (BrandPalette);
 *   - come APPLICARLA (applyBrandPalette → CSS var; createAppTheme in theme.ts
 *     → theme MUI, perché MUI hardcoda i colori e non legge le var());
 *   - una palette di DEFAULT di sicurezza (fallback se il backend non risponde
 *     o non ha un brand per il tenant);
 *   - una CACHE (localStorage) per riapplicare subito l'ultima palette ed
 *     evitare il "flash" al reload prima che arrivi la risposta.
 */

export interface BrandPalette {
  /** Colore primario brand → CSS var --primary. */
  primary: string;
  /** Testo/icone SOPRA il primario → CSS var --color-primary. */
  colorPrimary: string;
  /** Colore secondario brand → CSS var --secondary. */
  secondary: string;
  /** Testo/icone SOPRA il secondario → CSS var --color-secondary. */
  colorSecondary: string;
  /** Sfondo stato hover → CSS var --hover. */
  hover: string;
  /** Testo/icone in stato hover → CSS var --color-hover. */
  colorHover: string;
  /** Sfondo elemento selezionato/attivo → CSS var --selected. */
  selected: string;
  /** Sfondo header (topbar) → CSS var --header-bg. */
  headerBg: string;
  /** Testo/icone header → CSS var --header-color. */
  headerColor: string;
}

/**
 * Palette di default: il giallo storico D'vel, gli stessi due colori scritti in
 * `_variables.scss`. I due file servono momenti diversi — il CSS vale prima che
 * il JavaScript giri, questa costante serve a MUI, che le CSS var non le legge —
 * quindi vanno tenuti allineati.
 *
 * Vale quando il backend risponde a colori vuoti, cioè per un tenant che non ha
 * configurato la propria palette. Il backend non conosce colori di default: la
 * scelta di che cosa mostrare in quel caso è tutta qui. Il resto della palette
 * lo calcola `derivaPalette`.
 */
export const DEFAULT_PALETTE: BrandPalette = derivaPalette({
  primary: '#E5DE09',
  secondary: '#FBF8C5',
});

// Mappa chiave BrandPalette → nome CSS custom property (nomi letterali). Le
// var legacy (--color-sidebar-bg, --color-action-hover, --color-primary-light,
// --color-on-primary) derivano da queste in _variables.scss.
const CSS_VARS: Record<keyof BrandPalette, string> = {
  primary: '--primary',
  colorPrimary: '--color-primary',
  secondary: '--secondary',
  colorSecondary: '--color-secondary',
  hover: '--hover',
  colorHover: '--color-hover',
  selected: '--selected',
  headerBg: '--header-bg',
  headerColor: '--header-color',
};

/** Applica la palette scrivendo le CSS var brand su :root (inline, vince sul CSS). */
export function applyBrandPalette(palette: BrandPalette): void {
  const root = document.documentElement;
  (Object.keys(palette) as (keyof BrandPalette)[]).forEach((key) =>
    root.style.setProperty(CSS_VARS[key], palette[key]),
  );
}

// ─── Derivazione palette da un seme minimo ────────────────────────────────
//
// Idea: il tenant configura SOLO due colori (primario e secondario); tutto il
// resto (i testi sopra, selected, hover, header…) lo calcoliamo qui in JS. Due
// vantaggi:
//   - pochi campi da compilare lato backend (il default è un paracadute, non
//     un override: qualunque colore il cliente scelga viene elaborato su QUEL
//     colore, non sostituito dal giallo D'vel);
//   - la derivazione avviene in JS, non con CSS `color-mix`, quindi il
//     risultato è un hex concreto e funziona anche sui browser che non
//     supportano `color-mix`.

/**
 * I colori essenziali del tenant: si configurano dal CRM (Impostazioni →
 * Azienda → Paletta), e da qui si deriva tutto il resto.
 *
 * Il secondario e' FACOLTATIVO: chi non ha un colore d'accento lascia il campo
 * vuoto e gli accenti diventano una versione piu' chiara del primario, cosi'
 * l'interfaccia resta monocromatica sul brand invece di prendersi un verde a
 * caso.
 *
 * Perché due e non quattro: dopo il restyle il secondario non fa mai da sfondo
 * pieno — compare come `--gold-soft` (tinta chiara) con testo `--gold` (tinta
 * scura), entrambi derivati da lui — quindi non c'e' niente di cui dichiarare
 * il colore del testo sopra. Il testo sul primario invece serve (bottoni, chip,
 * avatar, topbar sono riempimenti pieni), ma bianco o nero lo decide il
 * contrasto, non il gusto: lo calcola `readableInk`.
 */
export type BrandSeed = { primary: string; secondary?: string };

/**
 * Quanto primario resta nel secondario derivato: 70% colore + 30% bianco. Una
 * schiaritura leggera — l'accento si distingue dal primario ma si legge come lo
 * stesso brand. Piu' chiaro di cosi' e le pastiglie accento sbiadirebbero sulla
 * carta; meno, e sarebbero indistinguibili dal primario.
 */
const SECONDARY_LIGHTEN = 0.7;

/**
 * Il secondario che si otterrebbe da questo primario se non ne fosse scelto uno.
 * Serve anche al form delle impostazioni per capire se il colore mostrato e'
 * una scelta o una derivazione, e quindi se il campo va lasciato vuoto.
 */
export function derivedSecondary(primary: string): string {
  return mixSrgb(primary, '#ffffff', SECONDARY_LIGHTEN);
}

interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

/** Parsa `#rgb`, `#rrggbb`, `rgb()` / `rgba()` in canali 0..1 + alpha 0..1. */
function parseColor(input: string): Rgba {
  const s = input.trim();
  if (s.startsWith('#')) {
    const h = s.length === 4 ? s.slice(1).replace(/./g, (c) => c + c) : s.slice(1);
    return {
      r: parseInt(h.slice(0, 2), 16) / 255,
      g: parseInt(h.slice(2, 4), 16) / 255,
      b: parseInt(h.slice(4, 6), 16) / 255,
      a: 1,
    };
  }
  const n = s.match(/[-\d.]+/g)?.map(Number) ?? [];
  return { r: (n[0] ?? 0) / 255, g: (n[1] ?? 0) / 255, b: (n[2] ?? 0) / 255, a: n[3] ?? 1 };
}

/** Serializza in CSS: hex se opaco, altrimenti `rgba()`. */
function toCss({ r, g, b, a }: Rgba): string {
  const ch = (x: number) => Math.max(0, Math.min(255, Math.round(x * 255)));
  const [R, G, B] = [ch(r), ch(g), ch(b)];
  if (a >= 0.999) {
    const hex = (v: number) => v.toString(16).padStart(2, '0');
    return `#${hex(R)}${hex(G)}${hex(B)}`;
  }
  return `rgba(${R}, ${G}, ${B}, ${Number(a.toFixed(3))})`;
}

/**
 * Miscela `a` e `b` in sRGB con peso `weightA` (0..1) su `a` — equivalente a
 * `color-mix(in srgb, a weightA, b)`. Premoltiplica per l'alpha, così un
 * colore semitrasparente si comporta come nel browser.
 *
 * È il mattone riusabile: quando la nuova UI introdurrà le tinte accento
 * (soft/ink/strong), si calcolano da qui senza CSS `color-mix`.
 */
export function mixSrgb(a: string, b: string, weightA: number): string {
  const c1 = parseColor(a);
  const c2 = parseColor(b);
  const w1 = weightA;
  const w2 = 1 - weightA;
  const outA = w1 * c1.a + w2 * c2.a;
  const chan = (k: 'r' | 'g' | 'b'): number => {
    const premult = w1 * c1[k] * c1.a + w2 * c2[k] * c2.a;
    return outA > 1e-6 ? premult / outA : 0;
  };
  return toCss({ r: chan('r'), g: chan('g'), b: chan('b'), a: outA });
}

/** Luminanza relativa (WCAG) 0..1, per scegliere testo chiaro/scuro sopra uno sfondo. */
function luminance(color: string): number {
  const { r, g, b } = parseColor(color);
  const lin = (v: number): number => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Nero o bianco, quale dei due contrasta meglio sullo sfondo dato. */
function readableInk(bg: string): string {
  return luminance(bg) > 0.42 ? '#131313' : '#ffffff';
}

/**
 * Deriva la palette COMPLETA da un seme minimo. I campi non-essenziali si
 * calcolano dai due colori scelti; un campo già valorizzato (override esplicito
 * dal backend) viene rispettato — si calcola SOLO ciò che manca o è vuoto.
 * `||` e non `??` di proposito: dal backend un colore non impostato arriva come
 * stringa VUOTA, non come null, e una stringa vuota va derivata come se
 * mancasse (altrimenti finirebbe in una CSS var, spegnendo il colore).
 *
 * Nota sui pesi: `selected`/`headerBg` sono tinte chiare del primario
 * (mix col bianco); riproducono da vicino i valori storici di D'vel
 * (#faf7c0 / #fbf8c5) partendo dal giallo, e si adattano a qualunque brand.
 */
export function derivaPalette(seed: BrandSeed & Partial<BrandPalette>): BrandPalette {
  const colorPrimary = seed.colorPrimary || readableInk(seed.primary);
  // Secondario non scelto: una schiaritura del primario (vedi SECONDARY_LIGHTEN).
  const secondary = seed.secondary || derivedSecondary(seed.primary);
  const selected = seed.selected || mixSrgb(seed.primary, '#ffffff', 0.2);
  const headerBg = seed.headerBg || mixSrgb(seed.primary, '#ffffff', 0.18);
  return {
    primary: seed.primary,
    colorPrimary,
    secondary,
    colorSecondary: seed.colorSecondary || readableInk(secondary),
    hover: seed.hover || 'rgba(0, 0, 0, 0.04)',
    colorHover: seed.colorHover || colorPrimary,
    selected,
    headerBg,
    headerColor: seed.headerColor || readableInk(headerBg),
  };
}

// ─── Cache anti-flash (localStorage) ──────────────────────────────────────

// La versione nella chiave serve a invalidare in blocco le cache già presenti
// sui browser degli utenti: le voci scritte sotto le chiavi precedenti non
// vengono più lette, e nessuno deve scrivere codice di migrazione. Si può fare
// perché quel che si perde è un caricamento senza anti-flash, non un dato.
//   v2 — cambio di palette del concept: via le palette blu di test.
//   v3 — i predefiniti sono passati al giallo D'vel: chi aveva in cache una
//        palette derivata dal vecchio default blu vedrebbe un lampo blu prima
//        che il backend risponda.
const CACHE_KEY = 'crm.brandPalette.v3';

/** Legge l'ultima palette applicata dalla cache. null se assente/corrotta. */
export function readCachedPalette(): BrandPalette | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    // Validazione minima: deve avere almeno il primario come stringa.
    return p && typeof p.primary === 'string' ? (p as BrandPalette) : null;
  } catch {
    return null;
  }
}

/** Salva la palette in cache per il prossimo avvio (anti-flash). */
export function cachePalette(palette: BrandPalette): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(palette));
  } catch {
    /* storage non disponibile: niente cache, non bloccante. */
  }
}
